import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import type React from 'react';
import { useEffect, useState } from 'react';
import { type FieldPath, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { z } from 'zod';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FancyMultiSelect } from '@/components/ui/fancy-multi-select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/NewButton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { approvedTenants } from '@/config/approved-tenants';
import useMatters from '@/hooks/useMatters';
import { getJurisdiction } from '@/jurisdictions';
import { REGIONS, getSubregionConfig, regionRequiresForum } from '@/jurisdictions/regions';
import { apiErrorMessage, apiValidationFieldErrors, backendClient } from '@/lib/api-client';
import { useAuth } from '@/lib/auth/hooks/auth-provider';
import { getEnvConfig } from '@/lib/env-config';

const step1Schema = z.object({
  name: z.string().trim().min(1, { message: "Name is required." }).max(75, { message: "Name must be at most 75 characters." }),
  matter_no: z.string().trim().max(25, { message: "Number must be at most 25 characters." }).optional(),
  case_reference: z.string().optional(),
  venue: z.string().optional(),
  region: z.string().trim().min(1, { message: "Region is required." }),
  subregion: z.string().trim().optional(),
  forum: z.string().trim().optional(),
  governing_law: z.string().optional(),
}).superRefine((data, ctx) => {
  if (regionRequiresForum(data.region) && !data.forum?.trim()) {
    ctx.addIssue({
      code: "custom",
      message: "Forum is required for this region",
      path: ["forum"],
    });
  }
  if (getSubregionConfig(data.region).required && !data.subregion?.trim()) {
    ctx.addIssue({
      code: "custom",
      message: "Subregion is required for this region",
      path: ["subregion"],
    });
  }
});

type Step1FormData = z.infer<typeof step1Schema>;
type Step1FieldName = FieldPath<Step1FormData>;

const step1FieldNames = [
  'name',
  'matter_no',
  'case_reference',
  'venue',
  'region',
  'subregion',
  'forum',
  'governing_law',
] as const satisfies readonly Step1FieldName[];
const step1Fields = new Set<string>(step1FieldNames);

const getDefaultRegion = (tenantId: string | undefined): "UK" | "US" => {
  if (!tenantId) return "UK";
  const tenantConfigs = approvedTenants[getEnvConfig().environment];
  const tenantName = tenantConfigs?.[tenantId];
  const usTenants = ["crimsonus", "hexagon", "fbfk", "gruenstein", "webergal", "ramllp"];
  return usTenants.includes(tenantName || "") ? "US" : "UK";
};

interface CreateMatterDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateMatterDialog: React.FC<CreateMatterDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { mutateMatters } = useMatters(false);
  const { setSelectedCase } = useCaseContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tenantId = user?.tenantId;
  const defaultRegion = getDefaultRegion(tenantId);

  const form = useForm<Step1FormData>({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      name: "",
      matter_no: "",
      case_reference: "",
      venue: "",
      region: defaultRegion,
      subregion: getSubregionConfig(defaultRegion).options[0] ?? "",
      forum: "Litigation",
    },
    mode: "onBlur",
  });

  const watchRegion = form.watch("region");
  const watchForum = form.watch("forum");
  const jurisdiction = getJurisdiction(watchRegion, watchForum);
  const subregionConfig = getSubregionConfig(watchRegion);
  const showForum = regionRequiresForum(watchRegion);

  useEffect(() => {
    form.setValue("subregion", getSubregionConfig(watchRegion).options[0] ?? "");
    if (!regionRequiresForum(watchRegion)) {
      form.setValue("forum", undefined);
    } else if (!form.getValues("forum")) {
      form.setValue("forum", "Litigation");
    }
  }, [watchRegion, form]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  async function onSubmit(values: Step1FormData) {
    const trimmedName = values.name.trim();
    const trimmedMatterNo = values.matter_no?.trim() ?? "";

    if (!trimmedName) {
      form.setError('name', { message: 'Name is required.' });
      return;
    }

    try {
      setIsSubmitting(true);

      const submitData: Record<string, unknown> = {
        ...values,
        name: trimmedName,
        matter_no: trimmedMatterNo || null,
        subregion: values.subregion?.trim() || null,
        forum: regionRequiresForum(values.region) ? values.forum : null,
      };

      const response = await backendClient.post('/matter', submitData);
      const newMatter = response.data;

      form.reset();
      mutateMatters();
      setSelectedCase(newMatter.guid);

      toast.success("Matter created successfully");
      onClose();
      await navigate({
        to: '/matter/$slug/case-files',
        params: { slug: newMatter.guid },
      });
    } catch (error: unknown) {
      console.error("Error creating matter:", error);
      const errorMessage = apiErrorMessage(error) || 'An error occurred while creating the matter.';

      let firstInvalidField: Step1FieldName | null = null;
      for (const fieldError of apiValidationFieldErrors(error)) {
        if (!step1Fields.has(fieldError.field)) continue;
        const field = fieldError.field as Step1FieldName;
        form.setError(field, { message: fieldError.message });
        firstInvalidField ??= field;
      }

      if (firstInvalidField) {
        form.setFocus(firstInvalidField);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create matter</DialogTitle>
          <DialogDescription>
            Fill out the form below to create a new matter.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="md:pt-2 space-y-4 md:space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel className="md:block md:pb-0.5">Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter the matter name" maxLength={75} {...field} />
                  </FormControl>
                  {(fieldState.isTouched || form.formState.isSubmitted) && <FormMessage />}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="matter_no"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel className="md:block md:pb-0.5">Matter number <span className="text-sm font-normal text-muted-foreground">(optional)</span></FormLabel>
                  <FormControl>
                    <Input placeholder="Enter the matter number" maxLength={25} {...field} />
                  </FormControl>
                  {(fieldState.isTouched || form.formState.isSubmitted) && <FormMessage />}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="case_reference"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="md:block md:pb-0.5">Case reference <span className="text-sm font-normal text-muted-foreground">(optional)</span></FormLabel>
                  <FormControl>
                    <Input placeholder="Optional case reference" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="region"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="md:block md:pb-0.5">Region</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a region" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {REGIONS.map((r) => (
                        <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {subregionConfig.options.length > 0 && (
              <FormField
                control={form.control}
                name="subregion"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel className="md:block md:pb-0.5">Venue</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a Venue" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {subregionConfig.options.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(fieldState.isTouched || form.formState.isSubmitted) && <FormMessage />}
                  </FormItem>
                )}
              />
            )}

            {showForum && (
              <FormField
                control={form.control}
                name="forum"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel className="md:block md:pb-0.5">Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Arbitration">Arbitration</SelectItem>
                        <SelectItem value="Litigation">Litigation</SelectItem>
                      </SelectContent>
                    </Select>
                    {(fieldState.isTouched || form.formState.isSubmitted) && <FormMessage />}
                  </FormItem>
                )}
              />
            )}

            {jurisdiction.form_options.venues.length > 0 && (
              <FormField
                control={form.control}
                name="venue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="md:block md:pb-0.5">Venue <span className="text-sm font-normal text-muted-foreground">(optional)</span></FormLabel>
                    <FormControl>
                      <FancyMultiSelect
                        value={field.value ? [{ value: field.value, label: field.value }] : []}
                        onValueChange={(values) => {
                          field.onChange(values.length > 0 ? values[0].value : "");
                        }}
                        placeholder="Select or enter venue"
                        options={jurisdiction.form_options.venues.map(opt => ({ value: opt, label: opt }))}
                        freeText={true}
                        singleSelect={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting} text='Cancel' />
              <Button type="submit" disabled={isSubmitting} loading={isSubmitting} loadingText='Creating' text='Create matter' />
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
